import {
    Avatar,
    AvatarProps,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormHelperText,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import 'cropperjs/dist/cropper.css';
import { useFieldValue } from 'ra-core';
import { createRef, useCallback, useState } from 'react';
import { FieldProps, Toolbar } from 'react-admin';
import { Cropper, ReactCropperElement } from 'react-cropper';
import { useDropzone } from 'react-dropzone';
import { useFormContext, useWatch } from 'react-hook-form';
import { DialogCloseButton } from './DialogCloseButton';

const ImageEditorField = (props: ImageEditorFieldProps) => {
    const { control } = useFormContext();
    const source = useWatch({
        control,
        name: props.source,
        defaultValue: undefined,
    });
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const {
        type = 'image',
        emptyText,
        helperText,
        linkPosition = 'none',
        readOnly = false,
    } = props;

    // If there's a form field value (uploaded image), use that
    // Otherwise, if there's a src prop (existing image), use that
    const src = source?.src || props.src;

    const commonProps = {
        src: src ?? undefined,
        ...(!readOnly && {
            onClick: () => setIsDialogOpen(true),
            style: { cursor: 'pointer' },
        }),
        sx: {
            ...props.sx,
            width: props.width || (type === 'avatar' ? 50 : undefined),
            height: props.height || (type === 'avatar' ? 50 : 200),
            borderRadius: type === 'avatar' ? '50%' : undefined,
        },
    };

    return (
        <>
            <Stack
                direction={linkPosition === 'right' ? 'row' : 'column'}
                alignItems={'center'}
                gap={linkPosition === 'right' ? 2 : 0.5}
                borderRadius={1}
                p={props.backgroundImageColor ? 1 : 0}
                sx={{
                    backgroundColor:
                        props.backgroundImageColor || 'transparent',
                }}
            >
                {src ? (
                    <Box component={'img'} {...commonProps} />
                ) : (
                    <Box
                        sx={commonProps.sx}
                        {...(!readOnly && {
                            onClick: commonProps.onClick,
                            style: commonProps.style,
                        })}
                    >
                        <Avatar sx={{ width: '100%', height: '100%' }}>
                            {emptyText}
                        </Avatar>
                    </Box>
                )}
                {linkPosition !== 'none' && !readOnly && (
                    <Typography
                        component={Link}
                        variant="caption"
                        onClick={() => setIsDialogOpen(true)}
                        textAlign="center"
                        sx={{ display: 'inline', cursor: 'pointer' }}
                    >
                        Change
                    </Typography>
                )}
            </Stack>
            {helperText !== false && helperText ? (
                <FormHelperText sx={{ mx: 0 }}>{helperText}</FormHelperText>
            ) : null}
            {!readOnly && (
                <ImageEditorDialog
                    open={isDialogOpen}
                    onClose={() => setIsDialogOpen(false)}
                    {...props}
                />
            )}
        </>
    );
};

const ImageEditorDialog = (props: ImageEditorDialogProps) => {
    const { setValue, handleSubmit } = useFormContext();
    const cropperRef = createRef<ReactCropperElement>();
    const initialValue = useFieldValue(props);
    const [file, setFile] = useState<File | undefined>();
    const [imageSrc, setImageSrc] = useState<string | undefined>(
        initialValue?.src
    );
    const onDrop = useCallback((files: File[]) => {
        const preview = URL.createObjectURL(files[0]);
        setFile(files[0]);
        setImageSrc(preview);
    }, []);

    const updateImage = async () => {
        if (props.type === 'avatar') {
            // For avatar type, use cropping
            const cropper = cropperRef.current?.cropper;
            const canvas = cropper?.getCroppedCanvas();
            const croppedImage = canvas?.toDataURL();
            if (croppedImage && canvas) {
                setImageSrc(croppedImage);

                const blob = await new Promise<Blob | null>(resolve =>
                    canvas.toBlob(resolve)
                );

                if (blob) {
                    const originalFile =
                        file ?? new File([], initialValue?.src);
                    const newFile = new File(
                        [blob],
                        originalFile.name || 'avatar.png',
                        {
                            type: blob.type,
                        }
                    );

                    setValue(
                        props.source,
                        {
                            src: croppedImage,
                            title: newFile.name,
                            rawFile: newFile,
                        },
                        { shouldDirty: true }
                    );
                    props.onClose();

                    if (props.onSave) {
                        handleSubmit(props.onSave)();
                    }
                }
            }
        } else {
            // For non-avatar type, use the image directly without cropping
            if (imageSrc) {
                const newFile = file ?? new File([], initialValue?.src);
                setValue(
                    props.source,
                    {
                        src: imageSrc,
                        title: newFile.name,
                        rawFile: newFile,
                    },
                    { shouldDirty: true }
                );
                props.onClose();

                if (props.onSave) {
                    handleSubmit(props.onSave)();
                }
            }
        }
    };

    const deleteImage = () => {
        setValue(props.source, null, { shouldDirty: true });
        if (props.onSave) {
            handleSubmit(props.onSave)();
        }
        props.onClose();
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'image/*': [
                '.jpeg',
                '.jpg',
                '.png',
                '.gif',
                '.webp',
                '.avif',
                '.svg',
            ],
        },
        onDrop,
        maxFiles: 1,
    });

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            maxWidth="md"
        >
            {props.type === 'avatar' && (
                <style>
                    {`
                        .cropper-crop-box,
                        .cropper-view-box {
                            border-radius: 50%;
                        }
                    `}
                </style>
            )}
            <DialogCloseButton onClose={props.onClose} />
            <DialogTitle>Upload and resize image</DialogTitle>
            <DialogContent>
                <Stack gap={2} justifyContent="center">
                    <Stack
                        direction="row"
                        justifyContent="center"
                        {...getRootProps()}
                        sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            cursor: 'pointer',
                        }}
                    >
                        <input {...getInputProps()} />
                        <p>Drop a file to upload, or click to select it.</p>
                    </Stack>
                    {props.type === 'avatar' ? (
                        <Cropper
                            ref={cropperRef}
                            src={imageSrc}
                            aspectRatio={1}
                            height={400}
                        />
                    ) : (
                        imageSrc && (
                            <Box
                                component="img"
                                src={imageSrc}
                                sx={{
                                    maxWidth: '100%',
                                    maxHeight: 400,
                                    objectFit: 'contain',
                                }}
                            />
                        )
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 0 }}>
                <Toolbar
                    sx={{ width: '100%', justifyContent: 'space-between' }}
                >
                    <Button variant="contained" onClick={updateImage}>
                        Update Image
                    </Button>
                    <Button variant="text" color="error" onClick={deleteImage}>
                        Delete
                    </Button>
                </Toolbar>
            </DialogActions>
        </Dialog>
    );
};

export default ImageEditorField;

export interface ImageEditorFieldProps<
    RecordType extends Record<string, any> = Record<string, any>,
> extends FieldProps<RecordType>,
        AvatarProps {
    width?: number;
    minWidth?: number;
    height?: number;
    minHeight?: number;
    type?: 'avatar' | 'image';
    onSave?: any;
    linkPosition?: 'right' | 'bottom' | 'none';
    backgroundImageColor?: string;
    readOnly?: boolean;
    helperText?: string | false;
}

export interface ImageEditorDialogProps extends ImageEditorFieldProps {
    open: boolean;
    onClose: () => void;
}
